import c from 'classnames';
import Image from 'next/image';
import React from 'react';
import { ThirdPartyIframe } from '../../ThirdPartyIframe/ThirdPartyIframe';
import { Video } from '../../atoms/Video/Video';
import { VERIFIED_SOLUTION_OVERLAY_URL } from './Gallery.constants';

export enum AssetMediaType {
  SCREENSHOT = 'SCREENSHOT',
  YOUTUBE = 'YOUTUBE',
  ATTACHMENT_VIDEO = 'ATTACHMENT_VIDEO',
  ATTACHMENT_AUDIO = 'ATTACHMENT_AUDIO',
  VIMEO = 'VIMEO',
  SOUNDCLOUD = 'SOUNDCLOUD',
  SKETCHFAB = 'SKETCHFAB',
  KEY_IMAGE = 'KEY_IMAGE',
  UNKNOWN = 'UNKNOWN',
}

export type GallerySlideData = {
  type: AssetMediaType;
  src: string;
  thumbnail?: string;
  thumbnailLabel?: string;
  alt?: string;
};

type GallerySlideProps = {
  slide: GallerySlideData;
  position?: 'details' | 'fullscreen-preview';
  showVerifiedSolutionOverlay?: boolean;
  /*
   * Hide all carousel items that are not currently the active item from screen readers.
   * */
  isHidden?: boolean;
  className?: string;
  style?: React.CSSProperties;
  hasCookieConsent: boolean;
  id?: string;
  videoCookieOverlayMicrocopy: {
    description: string;
    buttonLabel: string;
  };
  onOpenCookieSettings: () => void;
};

const GallerySlide = ({
  slide,
  position = 'details',
  showVerifiedSolutionOverlay = false,
  isHidden = false,
  className = '',
  style,
  id,
  hasCookieConsent,
  videoCookieOverlayMicrocopy,
  onOpenCookieSettings,
}: GallerySlideProps) => {
  const sizes =
    position === 'details'
      ? '(max-width: 1024px) 100vw, (max-width: 1240px): 60vw, 710px'
      : '100vw';
  const isEmbedVideo =
    slide.type === AssetMediaType.YOUTUBE ||
    slide.type === AssetMediaType.VIMEO;
  const hasVideoControls =
    isEmbedVideo || slide.type === AssetMediaType.ATTACHMENT_VIDEO;
  const label = slide.thumbnailLabel ?? slide.alt;

  return (
    <div
      id={id}
      data-test={id}
      style={style}
      className={c(
        'bg-gray-100 hide-inner-buttons-when-hidden relative aspect-video w-full shrink-0 grow-0 basis-full overflow-hidden rounded-thumbs lg:rounded-b-none lg:rounded-t-lg',
        { ['invisible']: isHidden },
        className
      )}
      aria-hidden={isHidden}
    >
      {isEmbedVideo && (
        <Video
          cookieConsent={hasCookieConsent}
          title={slide.alt}
          embedUrl={slide.src}
          videoCookieOverlayMicrocopy={videoCookieOverlayMicrocopy}
          isVisible={!isHidden}
          onOpenCookieSettings={onOpenCookieSettings}
        />
      )}
      {slide.type === AssetMediaType.SCREENSHOT && (
        <>
          <Image
            // displays a blurred version of the image behind, if the image does not fit in the aspect ratio properly the blurred version is visible behind
            fill
            src={slide.src}
            alt=""
            aria-hidden="true"
            sizes={sizes}
            className="pointer-events-none absolute top-0 scale-110 object-cover opacity-60 blur-md"
          />
          <Image
            fill
            src={slide.src}
            alt={label ?? ''}
            sizes={sizes}
            className="object-contain"
          />
        </>
      )}
      {slide.type !== AssetMediaType.SCREENSHOT && !isEmbedVideo && (
        <ThirdPartyIframe
          cookieConsent={hasCookieConsent}
          title={slide.alt}
          embedUrl={slide.src}
          cookieOverlayMicrocopy={videoCookieOverlayMicrocopy}
          isVisible={!isHidden}
          onOpenCookieSettings={onOpenCookieSettings}
        />
      )}
      {showVerifiedSolutionOverlay && (
        <div
          data-test="gallery-verified-solution-overlay"
          className={c(
            'pointer-events-none absolute inset-0',
            hasVideoControls && '-translate-y-[12%]' // account for video controls that are displayed at the bottom
          )}
        >
          <Image
            src={VERIFIED_SOLUTION_OVERLAY_URL}
            alt=""
            aria-hidden
            fill
            className="object-contain object-bottom"
          />
        </div>
      )}
    </div>
  );
};

export default GallerySlide;
